import {
  ApplicationSubmissionMethod,
  SubmissionType,
  isDefined,
} from '@zorro/clients';
import { formatDateTimeEnLocaleFromUtc } from '@zorro/shared/formatters';
import { useMonolithQuery, usePlanAttestations } from '@zorro/web/ui-utils';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { CarrierApplicationSubmitFormFields } from './CarrierCompleteStepUtils';

// Same wording as the activity log tab.
const ELECTION_SUBMITTED_BY_LABEL: Partial<Record<SubmissionType, string>> = {
  [SubmissionType.BY_EMPLOYEE]: 'Election submitted by employee',
  [SubmissionType.BY_EMPLOYER_ADMIN]:
    'Election submitted by the employer admin',
  [SubmissionType.BY_AGENT]: 'Election submitted by the enrollment agent',
  [SubmissionType.BY_OPERATOR]: 'Election submitted on behalf of the employee',
  [SubmissionType.SELF_REPORTED]: 'Election self reported by employee',
};

export type BrokerAttestationGate = {
  isAttestationGateActive: boolean;
  isBrokerAttestationAccepted: boolean;
  isMethodActionVisible: boolean;
  isSubmitLocked: boolean;
  brokerAttestation?: string;
  electionSubmittedNotice?: string;
};

type UseBrokerAttestation = (props: {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
}) => BrokerAttestationGate;

export const useBrokerAttestation: UseBrokerAttestation = ({
  employeeId,
  employerId,
  onboardingPeriodId,
  benefitId,
  submissionForm,
}) => {
  const selectedMethod = useWatch({
    control: submissionForm.control,
    name: 'submissionMethod',
  });
  const isBrokerAttestationAccepted = Boolean(
    useWatch({
      control: submissionForm.control,
      name: 'brokerAttestationAccepted',
    })
  );
  const { data: benefits, isLoading: isLoadingBenefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const benefit = benefits?.allBenefits.find(({ id }) => id === benefitId);
  const { attestations, isLoading: isLoadingAttestations } =
    usePlanAttestations({
      employeeId,
      employerId,
      plan: benefit?.plan,
      onboardingPeriodId,
    });
  const brokerAttestation = attestations?.brokerSignatureAttestation;

  const isHealthSherpaMethodSelected =
    selectedMethod === ApplicationSubmissionMethod.ENROLL_CONNECT ||
    selectedMethod === ApplicationSubmissionMethod.DEEPLINK;
  // Unresolved flag or plan reads as "no attestation" - lock, don't open.
  const isAwaitingAttestation =
    isHealthSherpaMethodSelected &&
    (isLoadingBenefits || isLoadingAttestations);
  // Stays visible after submission as a record of the affirmation.
  const isAttestationGateActive =
    isHealthSherpaMethodSelected && isDefined(brokerAttestation);
  const isMethodActionVisible =
    !isAwaitingAttestation &&
    (!isAttestationGateActive || isBrokerAttestationAccepted);

  const submissionType = benefit?.submissionType;
  const submittedByLabel =
    submissionType && ELECTION_SUBMITTED_BY_LABEL[submissionType];
  const electionSubmittedNotice =
    submittedByLabel &&
    (benefit?.submittedAt
      ? `${submittedByLabel} on ${formatDateTimeEnLocaleFromUtc(benefit.submittedAt)}`
      : submittedByLabel);

  return {
    isAttestationGateActive,
    isBrokerAttestationAccepted,
    isMethodActionVisible,
    isSubmitLocked:
      isAwaitingAttestation ||
      (isAttestationGateActive && !isBrokerAttestationAccepted),
    brokerAttestation,
    electionSubmittedNotice: electionSubmittedNotice || undefined,
  };
};
